import React, { useRef, useState, useLayoutEffect } from "react"
import { PaginatedTable } from "@ciq/shared/components/paginatedTable"
import { Button } from "@ciq/shared/components/buttons"
import { HoverTooltip, calculateTooltipPosition, PopUpPosition } from "@ciq/shared/components/tooltip"
import { SuccessToast, ErrorToast } from "@ciq/shared/utils/toast"
import { useZonePreviewData } from "./useZonePreviewData"
import { formatString } from "@ciq/shared/utils/format"

const COLUMNS = [
  { key: "zone_id", label: "Zone ID" },
  { key: "zone_name", label: "Zone Name" },
  { key: "square_footage", label: "Square Footage" },
  { key: "capacity", label: "Capacity" },
  { key: "rooms", label: "Rooms" }
]

/**
 * Format number with thousands separator.
 * @param {number} num
 * @returns {string}
 */
function formatNumber(num) {
  if (num == null) return ""
  return num.toLocaleString()
}

/**
 * Copy text to clipboard and show toast feedback.
 * @param {string} text - Text to copy
 */
function copyToClipboard(text) {
  navigator.clipboard.writeText(text)
    .then(() => SuccessToast("Copied to clipboard"))
    .catch(err => {
      console.error("Failed to copy:", err)
      ErrorToast("Failed to copy to clipboard")
    })
}

/**
 * Renders a truncated cell with hover tooltip showing full content.
 * Only shows tooltip if content is truncated.
 * Clicking the cell copies the full content to clipboard.
 * Uses smart positioning to avoid tooltip clipping near container edges.
 * @param {Object} props
 * @param {string} props.content - Display content (truncated by CSS)
 * @param {string} props.fullText - Full text for tooltip and copy
 * @param {string} props.initialPosition - Initial tooltip position from PopUpPosition enum
 * @returns {React.ReactNode}
 */
function TruncatedCell({ content, fullText, initialPosition }) {
  const ref = useRef(null)
  const [isTruncated, setIsTruncated] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState(initialPosition)

  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return

    const checkTruncation = () => {
      setIsTruncated(el.scrollHeight > el.clientHeight)
    }

    requestAnimationFrame(checkTruncation)

    const observer = new ResizeObserver(checkTruncation)
    observer.observe(el)
    return () => observer.disconnect()
  }, [content])

  const handleMouseEnter = () => {
    const el = ref.current
    if (!el) return

    // Use scroll-view as container - it stays fixed while content scrolls inside it
    const container = el.closest(".scroll-view")
    setTooltipPosition(calculateTooltipPosition(el, container, 100, initialPosition))
  }

  if (!fullText) return content || "--"

  const handleClick = () => copyToClipboard(fullText)

  // Always render same tree structure - use disabled prop instead of conditional wrapper
  // This prevents React from unmounting/remounting the span when isTruncated changes
  return (
    <HoverTooltip
      tooltip={fullText}
      position={tooltipPosition}
      delay={300}
      onClick={handleClick}
      disabled={!isTruncated}
    >
      <span ref={ref} className="truncated-cell" onMouseEnter={handleMouseEnter}>
        {content}
      </span>
    </HoverTooltip>
  )
}

/**
 * Custom cell renderer for zone data.
 * All cells are wrapped in TruncatedCell for consistent click-to-copy behavior.
 * @param {Object} row - Zone data row
 * @param {string} key - Column key
 * @returns {React.ReactNode}
 */
function renderCell(row, key) {
  switch (key) {
    case "square_footage":
    case "capacity": {
      const value = formatNumber(row[key])
      return <TruncatedCell content={value} fullText={value} initialPosition={PopUpPosition.Top} />
    }
    case "rooms": {
      const value = formatString([...(row.rooms || [])].sort().join(", "))
      return <TruncatedCell content={value} fullText={value} initialPosition={PopUpPosition.TopRight} />
    }
    default: {
      const value = formatString(row[key])
      return <TruncatedCell content={value} fullText={value} initialPosition={PopUpPosition.TopLeft} />
    }
  }
}

/**
 * @typedef {Object} DataPreviewTableProps
 * @property {string} schoolId - School identifier
 * @property {string} jobId - Job identifier
 */

/**
 * Zone data preview table component.
 * Fetches and displays zone data from S3 with search and pagination.
 *
 * @param {DataPreviewTableProps} props
 */
export function DataPreviewTable({ schoolId, jobId }) {
  const {
    data,
    totalCount,
    page,
    pageSize,
    searchQuery,
    isLoading,
    error,
    setPage,
    setPageSize,
    setSearchQuery,
    retry
  } = useZonePreviewData(schoolId, jobId, { initialPageSize: 10 })

  if (error) {
    return (
      <div className="data-preview-table data-preview-table--error">
        <div className="data-preview-table__error-message">
          {error}
        </div>
        <Button variant="secondary" onClick={retry}>
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="data-preview-table">
      <PaginatedTable
        columns={COLUMNS}
        data={data}
        totalCount={totalCount}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        searchValue={searchQuery}
        onSearch={setSearchQuery}
        renderCell={renderCell}
        rowKey="zone_id"
        isLoading={isLoading}
        emptyMessage="No zones found in the uploaded data."
        noResultsMessage="No zones match your search."
      />
    </div>
  )
}
